// THE FIVE SIGN-OFF VIEWS, RENDERED FROM THE REAL MODEL (owner 2026-11-01: "iso, section,
// top, west iso, east iso ... with only highlighted tiles and their fragments visible"):
// the viewer is turned to each angle, framed on the signed-off tiles, left to finish
// streaming, and read off as a JPEG. Nothing here decides what is shown - the viewer's
// isolate and tint do (SignoffViewCapture); this only points the camera and waits.
import { Plane, Vector3 } from "three";
import { tilesBox } from "./tileOverlay";

const P = Math.PI;
// azimuth / polar as camera-controls' rotateTo takes them (the viewer's STANDARD_VIEWS):
// iso as the viewer's own, the two side isos from the left (west) and right (east)
// looking down, top straight down, the section a level look from the front
export const ANGLES = [
  { kind: "iso", title: "Iso", az: P / 4, polar: P / 3 },
  { kind: "isoWest", title: "West iso", az: -P / 2, polar: P / 3 },
  { kind: "isoEast", title: "East iso", az: P / 2, polar: P / 3 },
  { kind: "top", title: "Top", az: 0, polar: 0.0001 },
  { kind: "section", title: "Section", az: 0, polar: P / 2 },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** The model's own drawn meshes - everything but the sign-off layer (tiles, grid, marks). */
function modelMeshes(viewer) {
  let n = 0;
  const skip = (o) => { for (let p = o; p; p = p.parent) if (p.name && p.name.startsWith("signoff:")) return true; return false; };
  if (viewer && viewer.scene) viewer.scene.traverse((o) => { if (o.isMesh && o.visible && !skip(o)) n += 1; });
  return n;
}

/**
 * Wait for the MODEL, not just the viewer: a streaming session, then its first drawn meshes.
 * The first staging capture began the moment the viewer existed - no streaming session yet,
 * so nothing was "busy" - and shot the tiles over an empty scene (2026-11-01). Waiting for
 * meshes also lets the stage's one-time "look from the top" on load happen before the angles.
 * Returns whether the model arrived within `maxMs`.
 */
export async function waitForModel(viewer, { maxMs = 180000, now = () => Date.now() } = {}) {
  const t0 = now();
  while (now() - t0 < maxMs) {
    const live = viewer && viewer.streaming && viewer.streaming.controller;
    if (live && modelMeshes(viewer) > 0) return true;
    // eslint-disable-next-line no-await-in-loop
    await sleep(500);
  }
  return false;
}

/** Wait until the viewer has fetched and drawn what the view needs: quiet for `quietMs`, at most `maxMs`. */
export async function settle(viewer, { quietMs = 1200, maxMs = 15000, now = () => Date.now() } = {}) {
  const t0 = now();
  let quietSince = null;
  while (now() - t0 < maxMs) {
    const busy = viewer && typeof viewer._isStreamingBusy === "function" ? viewer._isStreamingBusy() : false;
    if (busy) quietSince = null;
    else if (quietSince == null) quietSince = now();
    else if (now() - quietSince >= quietMs) return true;
    if (viewer && typeof viewer.requestRender === "function") viewer.requestRender(0);
    // eslint-disable-next-line no-await-in-loop
    await sleep(150);
  }
  return false;
}

/**
 * Where the camera goes for an angle: outside the tiles' bounding sphere, far enough for the
 * camera's field of view to hold all of it, looking at its centre. Placed with setLookAt -
 * turning with rotateTo and then fitting left the camera inside the tiles and two angles
 * identical on staging (2026-11-01). Y is up; azimuth and polar as camera-controls has them.
 */
export function cameraFor(box, angle, { fov = 45, aspect = 1.6, margin = 1.15 } = {}) {
  const c = box.getCenter(new Vector3());
  const r = Math.max(box.getSize(new Vector3()).length() / 2, 1);
  const vFov = (fov * Math.PI) / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const d = (r / Math.sin(Math.min(vFov, hFov) / 2)) * margin;
  const polar = Math.max(angle.polar, 0.0001);
  const dir = new Vector3(Math.sin(polar) * Math.sin(angle.az), Math.cos(polar), Math.sin(polar) * Math.cos(angle.az));
  return { position: c.clone().addScaledVector(dir, d), target: c, distance: d };
}

/** The box a sign-off view is cut to: the signed-off tiles and half a tile round them. */
export function clipBoxFor(grid, tiles) {
  return tilesBox(grid, tiles).expandByScalar(grid.edge * 0.5);
}

/** The six planes that keep what is inside `box` (three.js keeps the side a plane's normal points to). */
export function clipPlanes(box) {
  return [
    new Plane(new Vector3(1, 0, 0), -box.min.x), new Plane(new Vector3(-1, 0, 0), box.max.x),
    new Plane(new Vector3(0, 1, 0), -box.min.y), new Plane(new Vector3(0, -1, 0), box.max.y),
    new Plane(new Vector3(0, 0, 1), -box.min.z), new Plane(new Vector3(0, 0, -1), box.max.z),
  ];
}

/** Each angle, framed on `tiles`, as { kind, title, dataUrl }; `clip` (a Box3) cuts the picture to it. */
export async function captureAngles(viewer, grid, tiles, { quality = 0.85, angles = ANGLES, onShot = null, settleOpts = {}, clip = null } = {}) {
  const cc = viewer && viewer.cameraControls;
  if (!cc || !viewer.renderer || !tiles.length) return [];
  const before = viewer.renderer.clippingPlanes;
  if (clip) viewer.renderer.clippingPlanes = clipPlanes(clip);
  const box = tilesBox(grid, tiles);
  const cam = viewer.camera || {};
  const out = [];
  for (const a of angles) {
    const { position: p, target: t } = cameraFor(box, a, { fov: cam.fov || 45, aspect: cam.aspect || 1.6 });
    cc.setLookAt(p.x, p.y, p.z, t.x, t.y, t.z, false);
    if (typeof cc.update === "function") cc.update(1);
    // eslint-disable-next-line no-await-in-loop
    await settle(viewer, settleOpts);
    if (typeof cc.update === "function") cc.update(1);
    // drawn through the viewer's own frame path - but not its depth composite, which redraws
    // the scene with one material of its own that the clip planes may not reach - then read
    let drew = false;
    if (typeof viewer.drawFrame === "function") {
      try { drew = viewer.drawFrame({ moving: false, allowDirect: true, allowComposite: !clip }) !== "none"; } catch (e) { drew = false; }
    }
    if (!drew) viewer.renderer.render(viewer.scene, viewer.camera);
    out.push({ kind: a.kind, title: a.title, dataUrl: viewer.renderer.domElement.toDataURL("image/jpeg", quality) });
    if (onShot) onShot(out.length);
  }
  viewer.renderer.clippingPlanes = before || [];
  return out;
}
